// src/components/admin/feedback/FeedbackExportButton.jsx
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CircularProgress,
  Tooltip,
  Alert,
  Snackbar,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import {
  FileDownload,
  History,
  CheckCircle,
  Download,
  Refresh,
  Delete,
  Schedule,
  ArrowDropDown,
  ContentCopy
} from '@mui/icons-material';
import feedbackExportService from '../../../services/feedbackExportService';
import { selectFeedbackStats } from '../../../store/slices/adminFeedbackSlice';

const FeedbackExportButton = ({ variant = 'contained', size = 'medium' }) => {
  const stats = useSelector(selectFeedbackStats);
  
  const [exportInProgress, setExportInProgress] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [lastExport, setLastExport] = useState(null);
  const [exportHistory, setExportHistory] = useState(() => {
    // Зареди история от localStorage при инициализация
    const saved = localStorage.getItem('feedbackExportHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const newFeedbackCount = stats?.new || 0;

  const handleExport = async (type = 'new') => {
    if (exportInProgress) return;
    
    setExportInProgress(true);
    setAnchorEl(null); // Затвори менюто
    
    try {
      const response = await feedbackExportService.exportNewFeedbackToExcel();
      const blob = response.data;
      
      // Генериране на име на файл
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
      const filename = `Feedback_Export_${dateStr}_${timeStr}.xlsx`;
      
      // Създаване на линк за сваляне
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Записване на история
      const exportInfo = {
        id: Date.now(),
        filename,
        size: blob.size,
        timestamp: now.toISOString(),
        count: type === 'new' ? (stats?.new || 0) : (stats?.total || 0),
        type
      };
      
      setLastExport(exportInfo);
      const updatedHistory = [exportInfo, ...exportHistory.slice(0, 9)]; // Последните 10
      setExportHistory(updatedHistory);
      
      // Запази в localStorage
      localStorage.setItem('feedbackExportHistory', JSON.stringify(updatedHistory.slice(0, 10)));
      
      setSnackbar({
        open: true,
        message: `✅ Excel файлът "${filename}" е успешно свален (${formatFileSize(blob.size)})`,
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Export error:', error);
      
      let errorMessage = 'Грешка при експорт';
      if (error.response?.status === 404) {
        errorMessage = 'Ендпойнтът за експорт не е намерен';
      } else if (error.response?.status === 500) {
        errorMessage = 'Грешка на сървъра при генериране на Excel файл';
      } else if (error.response?.status === 403) {
        errorMessage = 'Нямате права за това действие';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Времето за изчакване изтече. Моля, опитайте отново.';
      } else if (error.message) {
        errorMessage = `Грешка: ${error.message}`;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setExportInProgress(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'току що';
    if (diffMins < 60) return `${diffMins} мин.`;
    if (diffHours < 24) return `${diffHours} ч.`;
    if (diffDays < 7) return `${diffDays} д.`;
    return past.toLocaleDateString('bg-BG');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCopyHistory = () => {
    const historyText = exportHistory
      .map(exp => `${exp.filename} - ${formatFileSize(exp.size)} - ${formatTimeAgo(exp.timestamp)}`)
      .join('\n');
    
    navigator.clipboard.writeText(historyText)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  const handleClearHistory = () => {
    setExportHistory([]);
    localStorage.removeItem('feedbackExportHistory');
    setSnackbar({
      open: true,
      message: 'Историята на експорти е изчистена',
      severity: 'info'
    });
    handleMenuClose();
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Основен бутон */}
        <Tooltip title={`Изтегли ${newFeedbackCount} нови фийдбак записа като Excel файл`}>
          <Button
            startIcon={exportInProgress ? <CircularProgress size={20} /> : <FileDownload />}
            onClick={() => handleExport('new')}
            variant={variant}
            color="success"
            size={size}
            disabled={exportInProgress || newFeedbackCount === 0}
            sx={{
              minWidth: variant === 'contained' ? 140 : 'auto',
              background: exportInProgress 
                ? '#757575' 
                : newFeedbackCount === 0
                  ? '#e0e0e0'
                  : variant === 'contained' 
                    ? 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)'
                    : undefined,
              color: exportInProgress || (newFeedbackCount === 0 && variant === 'contained') 
                ? '#9e9e9e' 
                : variant === 'contained' ? 'white' : undefined,
              '&:hover': {
                background: newFeedbackCount === 0 
                  ? '#e0e0e0' 
                  : variant === 'contained'
                    ? 'linear-gradient(45deg, #1b5e20 30%, #388e3c 90%)'
                    : undefined,
              },
              position: 'relative',
              overflow: 'hidden',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {exportInProgress ? (
              'Генерира...'
            ) : (
              <>
                {newFeedbackCount > 0 
                  ? `Excel (${newFeedbackCount})` 
                  : 'Няма нови'}
                {lastExport && variant === 'contained' && (
                  <CheckCircle 
                    sx={{ 
                      ml: 1, 
                      fontSize: 16,
                      opacity: 0.9
                    }} 
                  />
                )}
              </>
            )}
            
            {/* Индикатор за прогрес */}
            {exportInProgress && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #4caf50 0%, #81c784 100%)',
                  animation: 'progress 2s linear infinite',
                  '@keyframes progress': {
                    '0%': { width: '0%' },
                    '50%': { width: '50%' },
                    '100%': { width: '100%' }
                  }
                }}
              />
            )}
          </Button>
        </Tooltip>

        {/* Бутон за меню с опции */}
        {exportHistory.length > 0 && (
          <>
            <Tooltip title="История и опции">
              <Button
                onClick={handleMenuOpen}
                variant="outlined"
                size={size}
                sx={{
                  minWidth: 'auto',
                  px: 1
                }}
              >
                <ArrowDropDown />
              </Button>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 280,
                  maxHeight: 400,
                  overflow: 'auto'
                }
              }}
            >
              <MenuItem disabled>
                <ListItemText 
                  primary={
                    <Typography variant="subtitle2" fontWeight="600">
                      История на експорти
                    </Typography>
                  }
                  secondary={`${exportHistory.length} записа`}
                />
              </MenuItem>
              <Divider />
              
              {exportHistory.slice(0, 5).map((exp, index) => (
                <MenuItem key={exp.id} dense>
                  <ListItemIcon>
                    <Download fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="body2" noWrap>
                          {exp.filename}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip 
                            label={formatTimeAgo(exp.timestamp)}
                            size="small" 
                            variant="outlined"
                            icon={<Schedule fontSize="small" />}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(exp.size)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </MenuItem>
              ))}
              
              {exportHistory.length > 5 && (
                <MenuItem disabled>
                  <Typography variant="caption" color="text.secondary">
                    ... и още {exportHistory.length - 5} записа
                  </Typography>
                </MenuItem>
              )}
              
              <Divider />
              
              <MenuItem onClick={handleCopyHistory} dense>
                <ListItemIcon>
                  <ContentCopy fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Копирай историята" />
                {copySuccess && (
                  <CheckCircle color="success" fontSize="small" />
                )}
              </MenuItem>
              
              <MenuItem onClick={handleClearHistory} dense>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Изчисти историята" 
                  primaryTypographyProps={{ color: 'error' }}
                />
              </MenuItem>
              
              <Divider />
              
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">
                  Последен експорт: {lastExport ? formatTimeAgo(lastExport.timestamp) : 'няма'}
                </Typography>
              </MenuItem>
            </Menu>
          </>
        )}

        {/* Индикатор за нови записи */}
        {newFeedbackCount > 0 && variant === 'contained' && (
          <Chip
            label={`${newFeedbackCount} нови`}
            size="small"
            color="warning"
            variant="filled"
            sx={{
              fontWeight: 600,
              animation: newFeedbackCount > 0 ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.7 },
                '100%': { opacity: 1 }
              }
            }}
          />
        )}
      </Box>

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 8000 : 5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mb: 7 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
          icon={snackbar.severity === 'success' ? <FileDownload /> : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackExportButton;